/**
 * Schema View Component
 *
 * Displays and manages table schema with drag-and-drop reordering.
 * Uses TypeBadge for type selection and ConstraintToggle for required/unique.
 *
 * Features:
 * - Drag-and-drop field reordering via @dnd-kit
 * - TypeBadge with icon + dropdown
 * - ConstraintToggle badges (clickable)
 * - Footer with Add, Save, Cancel
 * - Track pending changes until Save
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Plus, Search, Eye, EyeOff } from 'lucide-react';
import { Skeleton, Button, Input, Checkbox, Toggle, cn } from '@loader/ui';
import type { FieldDataType, ConstraintType, TableFieldApiResponse } from '@loader/shared-types';

import { TypeBadge } from '@/features/schemas/components';
import { useResolvedConnectorType } from '@/features/workspace/hooks/useResolvedConnectorType';
import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';
import { AddFieldDialog } from '@/features/tables/components/dialogs';
import {
  useTableQuery,
  useTableActiveOperationQuery,
  useBatchChangeFieldTypeMutation,
  useChangeFieldConstraintMutation,
  useUpdateFieldConstraintsMutation,
  useReorderFieldsMutation,
  useUpdateFieldMutation,
  invalidateSchemaQueries,
} from '@/features/tables/hooks/useTableQueries';
import { toast } from '@/lib/toast';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// TYPES
// ============================================================================

interface SchemaViewProps {
  tableId: string;
  variant?: 'default' | 'flush';
  /** Whether user can edit - viewers see read-only schema */
  canEdit?: boolean;
}

interface FieldRow {
  id: string;
  name: string;
  dataType: FieldDataType;
  isEnabled: boolean;
  isRequired: boolean;
  isUnique: boolean;
  orderIndex: number;
  description: string | null;
}

interface PendingTypeChange {
  fieldId: string;
  fieldName: string;
  fromType: FieldDataType;
  toType: FieldDataType;
}

interface PendingConstraintChange {
  fieldId: string;
  constraintType: ConstraintType;
  toValue: boolean;
}

interface TypeConversionOperationParams {
  field_conversions?: Array<{
    field_id: string;
    to_type: FieldDataType;
    field_name?: string;
  }>;
}

interface ConstraintChangeOperationParams {
  field_id: string;
  constraint_type: ConstraintType;
  to_value: boolean;
}

// ============================================================================
// UTILITIES
// ============================================================================

function getConstraintKey(fieldId: string, constraintType: ConstraintType): string {
  return `${fieldId}-${constraintType}`;
}

function cleanupMapEntries<K, V>(
  map: Map<K, V>,
  setMap: React.Dispatch<React.SetStateAction<Map<K, V>>>,
  shouldRemove: (key: K, value: V) => boolean
): void {
  if (map.size === 0) return;

  const toRemove: K[] = [];
  for (const [key, value] of map) {
    if (shouldRemove(key, value)) {
      toRemove.push(key);
    }
  }

  if (toRemove.length > 0) {
    setMap((prev) => {
      const next = new Map(prev);
      for (const key of toRemove) {
        next.delete(key);
      }
      return next;
    });
  }
}

// ============================================================================
// SORTABLE FIELD ROW
// ============================================================================

interface SortableFieldRowProps {
  field: FieldRow;
  displayedType: FieldDataType;
  displayedEnabled: boolean;
  displayedRequired: boolean;
  displayedUnique: boolean;
  displayedDescription: string;
  hasTypeChange: boolean;
  hasEnabledChange: boolean;
  hasRequiredChange: boolean;
  hasUniqueChange: boolean;
  hasDescriptionChange: boolean;
  isDisabled: boolean;
  /** Type dropdown disabled separately (e.g. connector-managed tables) */
  isTypeDisabled: boolean;
  onTypeChange: (fieldId: string, newType: FieldDataType) => void;
  onEnabledToggle: (fieldId: string) => void;
  onConstraintToggle: (field: FieldRow, constraintType: ConstraintType) => void;
  onDescriptionChange: (fieldId: string, description: string) => void;
}

function SortableFieldRow({
  field,
  displayedType,
  displayedEnabled,
  displayedRequired,
  displayedUnique,
  displayedDescription,
  hasTypeChange,
  hasEnabledChange,
  hasRequiredChange,
  hasUniqueChange,
  hasDescriptionChange,
  isDisabled,
  isTypeDisabled,
  onTypeChange,
  onEnabledToggle,
  onConstraintToggle,
  onDescriptionChange,
}: SortableFieldRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'hover:bg-muted/50 border-b transition-colors',
        isDragging && 'bg-muted/30'
      )}
    >
      {/* Drag handle */}
      <td className="w-10 px-2 py-2">
        <div
          className={cn(
            'cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted',
            isDisabled && 'cursor-not-allowed opacity-50'
          )}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </td>

      {/* Field name */}
      <td className="text-foreground px-4 py-2 text-sm max-w-48">
        <span className="font-medium truncate block">{field.name}</span>
      </td>

      {/* Type */}
      <td className="px-4 py-2">
        <TypeBadge
          dataType={displayedType}
          onTypeChange={(type) => onTypeChange(field.id, type)}
          disabled={isTypeDisabled}
          isPending={hasTypeChange}
        />
      </td>

      {/* Enabled */}
      <td className="px-4 py-2 text-center">
        <Checkbox
          checked={displayedEnabled}
          onCheckedChange={() => onEnabledToggle(field.id)}
          disabled={isDisabled}
          className={cn(hasEnabledChange && 'ring-2 ring-primary/30')}
        />
      </td>

      {/* Required */}
      <td className="px-4 py-2 text-center">
        <Checkbox
          checked={displayedRequired}
          onCheckedChange={() => onConstraintToggle(field, 'required')}
          disabled={isDisabled || !displayedEnabled}
          className={cn(hasRequiredChange && 'ring-2 ring-primary/30')}
        />
      </td>

      {/* Unique */}
      <td className="px-4 py-2 text-center">
        <Checkbox
          checked={displayedUnique}
          onCheckedChange={() => onConstraintToggle(field, 'unique')}
          disabled={isDisabled || !displayedEnabled}
          className={cn(hasUniqueChange && 'ring-2 ring-primary/30')}
        />
      </td>

      {/* Description */}
      <td className="px-4 py-2">
        <Input
          value={displayedDescription}
          onChange={(e) => onDescriptionChange(field.id, e.target.value)}
          placeholder="Add description..."
          disabled={isDisabled}
          className={cn(
            'h-7 text-sm bg-transparent border-none shadow-none focus-visible:ring-1 focus-visible:ring-primary/30',
            hasDescriptionChange && 'ring-2 ring-primary/30'
          )}
        />
      </td>
    </tr>
  );
}

// ============================================================================
// DRAG OVERLAY ROW
// ============================================================================

interface DragOverlayRowProps {
  field: FieldRow;
}

function DragOverlayRow({ field }: DragOverlayRowProps) {
  const TypeIcon = getTypeIcon(field.dataType);

  return (
    <div className="flex items-center gap-3 bg-background border rounded-lg px-3 py-2 shadow-lg">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium text-sm">{field.name}</span>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <TypeIcon className="h-3.5 w-3.5" />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SchemaView({ tableId, variant = 'default', canEdit = true }: SchemaViewProps) {
  // Fetch table details
  const { data: tableDetails, isLoading } = useTableQuery(tableId);

  // Pending changes state
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingTypeChange>>(new Map());
  const [pendingConstraints, setPendingConstraints] = useState<Map<string, PendingConstraintChange>>(new Map());
  const [pendingEnabled, setPendingEnabled] = useState<Map<string, boolean>>(new Map());
  const [pendingDescriptions, setPendingDescriptions] = useState<Map<string, string>>(new Map());
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  // Saving state (mutation fired, waiting for cache to update)
  const [savingTypes, setSavingTypes] = useState<Map<string, FieldDataType>>(new Map());
  const [savingConstraints, setSavingConstraints] = useState<Map<string, boolean>>(new Map());
  const [savingEnabled, setSavingEnabled] = useState<Map<string, boolean>>(new Map());
  const [savingDescriptions, setSavingDescriptions] = useState<Map<string, string>>(new Map());

  // Awaiting confirmation state (operation completed, waiting for field data to confirm)
  // This prevents the "revert then back" visual glitch when operation completes but query hasn't refreshed
  const [awaitingTypeConfirmation, setAwaitingTypeConfirmation] = useState<Map<string, FieldDataType>>(new Map());
  const [awaitingConstraintConfirmation, setAwaitingConstraintConfirmation] = useState<{
    fieldId: string;
    constraintType: ConstraintType;
    toValue: boolean;
  } | null>(null);

  // DnD state
  const [activeId, setActiveId] = useState<string | null>(null);

  // Add field dialog state
  const [isAddFieldDialogOpen, setIsAddFieldDialogOpen] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Show/hide disabled fields (hidden by default)
  const [showDisabledFields, setShowDisabledFields] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Transform fields from API response
  const fields = useMemo((): FieldRow[] => {
    if (!tableDetails) return [];
    return (tableDetails.fields || [])
      .map((f: TableFieldApiResponse) => ({
        id: f.id,
        name: f.name,
        dataType: f.data_type as FieldDataType,
        isEnabled: f.is_enabled,
        isRequired: f.is_required,
        isUnique: f.is_unique,
        orderIndex: f.order_index,
        description: f.description,
      }))
      .sort((a: FieldRow, b: FieldRow) => a.orderIndex - b.orderIndex);
  }, [tableDetails]);

  // Ordered fields (with pending order applied)
  const orderedFields = useMemo(() => {
    if (!pendingOrder) return fields;
    return pendingOrder
      .map((id) => fields.find((f) => f.id === id))
      .filter((f): f is FieldRow => f !== undefined);
  }, [fields, pendingOrder]);

  // Filtered fields based on search and enabled status
  const filteredFields = useMemo(() => {
    let result = orderedFields;

    // Filter by enabled status
    if (!showDisabledFields) {
      result = result.filter((f) => f.isEnabled || pendingEnabled.get(f.id) === true);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(term) ||
          f.dataType.toLowerCase().includes(term) ||
          f.description?.toLowerCase().includes(term)
      );
    }

    return result;
  }, [orderedFields, searchTerm, showDisabledFields, pendingEnabled]);

  // Query client for manual invalidation
  const queryClient = useQueryClient();

  // Check for active operations
  const { data: activeOperation } = useTableActiveOperationQuery(tableId);
  const hasActiveOperation = !!activeOperation;

  // Track previous operation to detect completion
  const prevOperationRef = useRef<string | null>(null);

  // When operation completes, refresh table data and clear saving states
  useEffect(() => {
    const currentOpId = activeOperation?.id ?? null;
    const prevOpId = prevOperationRef.current;

    // Detect operation completion: had an operation, now it's gone or completed
    if (prevOpId && !currentOpId) {
      // Invalidate table detail to get fresh field data
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tables.detail(tableId),
      });
      // Clear saving states since operation completed
      setSavingTypes(new Map());
      setSavingConstraints(new Map());
      // Clear awaiting states - on failure, field data won't match target
      // so these would otherwise never clear and the overlay would stay visible
      setAwaitingTypeConfirmation(new Map());
      setAwaitingConstraintConfirmation(null);
    }

    prevOperationRef.current = currentOpId;
  }, [activeOperation?.id, tableId, queryClient]);

  // Extract target types from active type_conversion operation
  const activeConversions = useMemo((): Map<string, FieldDataType> => {
    const map = new Map<string, FieldDataType>();
    if (activeOperation?.operation_type === 'type_conversion' && activeOperation.params) {
      const params = activeOperation.params as unknown as TypeConversionOperationParams;
      if (params.field_conversions) {
        for (const fc of params.field_conversions) {
          map.set(fc.field_id, fc.to_type);
        }
      }
    }
    return map;
  }, [activeOperation]);

  // Extract target constraint from active constraint_change operation
  const activeConstraintChange = useMemo((): { fieldId: string; constraintType: ConstraintType; toValue: boolean } | null => {
    if (activeOperation?.operation_type === 'constraint_change' && activeOperation.params) {
      const params = activeOperation.params as unknown as ConstraintChangeOperationParams;
      return {
        fieldId: params.field_id,
        constraintType: params.constraint_type,
        toValue: params.to_value,
      };
    }
    return null;
  }, [activeOperation]);

  // Clear pending type changes when active operation picks them up
  useEffect(() => {
    if (activeConversions.size > 0 && pendingChanges.size > 0) {
      const allCovered = Array.from(pendingChanges.keys()).every(
        (fieldId) => activeConversions.has(fieldId)
      );
      if (allCovered) {
        setPendingChanges(new Map());
      }
    }
  }, [activeConversions, pendingChanges]);

  // Clear saving types when active operation picks them up
  useEffect(() => {
    if (activeConversions.size > 0 && savingTypes.size > 0) {
      const allCovered = Array.from(savingTypes.keys()).every(
        (fieldId) => activeConversions.has(fieldId)
      );
      if (allCovered) {
        setSavingTypes(new Map());
      }
    }
  }, [activeConversions, savingTypes]);

  // Store active conversions in awaiting state (persists after operation completes)
  useEffect(() => {
    if (activeConversions.size > 0) {
      setAwaitingTypeConfirmation(new Map(activeConversions));
    }
  }, [activeConversions]);

  // Store active constraint change in awaiting state (persists after operation completes)
  useEffect(() => {
    if (activeConstraintChange) {
      setAwaitingConstraintConfirmation({ ...activeConstraintChange });
    }
  }, [activeConstraintChange]);

  // Clear saving types when field data matches (operation completed)
  useEffect(() => {
    cleanupMapEntries(savingTypes, setSavingTypes, (fieldId, targetType) => {
      const field = fields.find((f) => f.id === fieldId);
      return field !== undefined && field.dataType === targetType;
    });
  }, [fields, savingTypes]);

  // Clear awaiting type confirmation when field data confirms the change
  useEffect(() => {
    if (awaitingTypeConfirmation.size === 0) return;

    const stillAwaiting = new Map<string, FieldDataType>();
    for (const [fieldId, targetType] of awaitingTypeConfirmation) {
      const field = fields.find((f) => f.id === fieldId);
      // Keep awaiting if field not found or type doesn't match yet
      if (!field || field.dataType !== targetType) {
        stillAwaiting.set(fieldId, targetType);
      }
    }

    if (stillAwaiting.size !== awaitingTypeConfirmation.size) {
      setAwaitingTypeConfirmation(stillAwaiting);
    }
  }, [fields, awaitingTypeConfirmation]);

  // Clear awaiting constraint confirmation when field data confirms the change
  useEffect(() => {
    if (!awaitingConstraintConfirmation) return;

    const { fieldId, constraintType, toValue } = awaitingConstraintConfirmation;
    const field = fields.find((f) => f.id === fieldId);

    if (field) {
      const currentValue = constraintType === 'unique' ? field.isUnique : field.isRequired;
      if (currentValue === toValue) {
        setAwaitingConstraintConfirmation(null);
      }
    }
  }, [fields, awaitingConstraintConfirmation]);

  // Clear saving constraints when field values match (operation completed)
  useEffect(() => {
    cleanupMapEntries(savingConstraints, setSavingConstraints, (key, targetValue) => {
      const lastDashIndex = key.lastIndexOf('-');
      const fieldId = key.substring(0, lastDashIndex);
      const constraintType = key.substring(lastDashIndex + 1) as ConstraintType;

      const field = fields.find((f) => f.id === fieldId);
      if (!field) return false;

      const actualValue = constraintType === 'unique' ? field.isUnique : field.isRequired;
      return actualValue === targetValue;
    });
  }, [fields, savingConstraints]);

  // Clear saving enabled when field values match (update completed)
  useEffect(() => {
    cleanupMapEntries(savingEnabled, setSavingEnabled, (fieldId, targetValue) => {
      const field = fields.find((f) => f.id === fieldId);
      return field !== undefined && field.isEnabled === targetValue;
    });
  }, [fields, savingEnabled]);

  // Clear saving descriptions when field values match (update completed)
  useEffect(() => {
    cleanupMapEntries(savingDescriptions, setSavingDescriptions, (fieldId, targetDescription) => {
      const field = fields.find((f) => f.id === fieldId);
      return field !== undefined && (field.description || '') === targetDescription;
    });
  }, [fields, savingDescriptions]);

  // Clear pending order when order matches
  useEffect(() => {
    if (!pendingOrder) return;
    const currentOrder = fields.map((f) => f.id);
    const orderMatches = pendingOrder.every((id, idx) => currentOrder[idx] === id);
    if (orderMatches) {
      setPendingOrder(null);
    }
  }, [fields, pendingOrder]);

  // Mutations
  const batchChangeTypeMutation = useBatchChangeFieldTypeMutation(tableId);
  const changeConstraintMutation = useChangeFieldConstraintMutation(tableId);
  const updateConstraintsMutation = useUpdateFieldConstraintsMutation(tableId);
  const reorderFieldsMutation = useReorderFieldsMutation(tableId);
  const updateFieldMutation = useUpdateFieldMutation(tableId);

  // Check if table is draft (draft tables use PUT /fields for constraint changes)
  const isDraftTable = tableDetails?.status === 'draft';

  const handleTypeChange = useCallback((fieldId: string, newType: FieldDataType) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    if (newType === field.dataType) {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(fieldId);
        return next;
      });
      return;
    }

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(fieldId, {
        fieldId,
        fieldName: field.name,
        fromType: field.dataType,
        toType: newType,
      });
      return next;
    });
  }, [fields]);

  const handleEnabledToggle = useCallback((fieldId: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const originalValue = field.isEnabled;

    setPendingEnabled((prev) => {
      const next = new Map(prev);
      const pendingValue = prev.get(fieldId);
      const displayedValue = pendingValue ?? originalValue;
      const newValue = !displayedValue;

      if (newValue === originalValue) {
        next.delete(fieldId);
      } else {
        next.set(fieldId, newValue);
      }
      return next;
    });
  }, [fields]);

  const handleConstraintToggle = useCallback((field: FieldRow, constraintType: ConstraintType) => {
    const originalValue = constraintType === 'unique' ? field.isUnique : field.isRequired;
    const key = getConstraintKey(field.id, constraintType);
    const savingValue = savingConstraints.get(key);

    setPendingConstraints((prev) => {
      const next = new Map(prev);
      const pending = prev.get(key);
      const displayedValue = pending?.toValue ?? savingValue ?? originalValue;
      const newValue = !displayedValue;

      if (newValue === originalValue) {
        next.delete(key);
      } else {
        next.set(key, {
          fieldId: field.id,
          constraintType,
          toValue: newValue,
        });
      }

      // Business rule enforcement: unique→required coupling
      if (constraintType === 'unique' && newValue === true) {
        // When enabling unique, also enable required
        const requiredKey = getConstraintKey(field.id, 'required');
        const requiredOriginal = field.isRequired;
        const requiredSaving = savingConstraints.get(requiredKey);
        const requiredPending = prev.get(requiredKey);
        const requiredDisplayed = requiredPending?.toValue ?? requiredSaving ?? requiredOriginal;

        // Only add if not already required
        if (!requiredDisplayed) {
          if (requiredOriginal === true) {
            next.delete(requiredKey);
          } else {
            next.set(requiredKey, {
              fieldId: field.id,
              constraintType: 'required',
              toValue: true,
            });
          }
        }
      } else if (constraintType === 'required' && newValue === false) {
        // When disabling required, also disable unique
        const uniqueKey = getConstraintKey(field.id, 'unique');
        const uniqueOriginal = field.isUnique;
        const uniqueSaving = savingConstraints.get(uniqueKey);
        const uniquePending = prev.get(uniqueKey);
        const uniqueDisplayed = uniquePending?.toValue ?? uniqueSaving ?? uniqueOriginal;

        // Only add if currently unique
        if (uniqueDisplayed) {
          if (uniqueOriginal === false) {
            next.delete(uniqueKey);
          } else {
            next.set(uniqueKey, {
              fieldId: field.id,
              constraintType: 'unique',
              toValue: false,
            });
          }
        }
      }

      return next;
    });
  }, [savingConstraints]);

  const handleDescriptionChange = useCallback((fieldId: string, newDescription: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const originalDescription = field.description || '';

    if (newDescription === originalDescription) {
      setPendingDescriptions((prev) => {
        const next = new Map(prev);
        next.delete(fieldId);
        return next;
      });
      return;
    }

    setPendingDescriptions((prev) => {
      const next = new Map(prev);
      next.set(fieldId, newDescription);
      return next;
    });
  }, [fields]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const currentOrder = pendingOrder || fields.map((f) => f.id);
    const oldIndex = currentOrder.indexOf(active.id as string);
    const newIndex = currentOrder.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(currentOrder, oldIndex, newIndex);

    // Check if new order matches original
    const originalOrder = fields.map((f) => f.id);
    const orderMatches = newOrder.every((id, idx) => originalOrder[idx] === id);

    setPendingOrder(orderMatches ? null : newOrder);
  }, [pendingOrder, fields]);

  const handleSave = async () => {
    if (pendingChanges.size === 0 && pendingConstraints.size === 0 && pendingEnabled.size === 0 && pendingDescriptions.size === 0 && !pendingOrder) return;

    // Save type changes
    if (pendingChanges.size > 0) {
      const typeChanges = Array.from(pendingChanges.values());
      const fieldConversions = typeChanges.map((change) => ({
        fieldId: change.fieldId,
        toType: change.toType,
      }));

      const newSavingTypes = new Map(savingTypes);
      for (const change of typeChanges) {
        newSavingTypes.set(change.fieldId, change.toType);
      }
      setSavingTypes(newSavingTypes);
      setPendingChanges(new Map());

      try {
        await batchChangeTypeMutation.mutateAsync(fieldConversions);
      } catch (error) {
        toast.operation.typeConversionFailed(
          error instanceof Error ? error.message : undefined
        );
        setSavingTypes((prev) => {
          const next = new Map(prev);
          for (const change of typeChanges) {
            next.delete(change.fieldId);
          }
          return next;
        });
      }
    }

    // Save constraint changes
    if (pendingConstraints.size > 0) {
      const constraintChanges = Array.from(pendingConstraints.values());

      const newSavingConstraints = new Map(savingConstraints);
      for (const change of constraintChanges) {
        newSavingConstraints.set(getConstraintKey(change.fieldId, change.constraintType), change.toValue);
      }
      setSavingConstraints(newSavingConstraints);
      setPendingConstraints(new Map());

      if (isDraftTable) {
        // Draft tables: batch update via PUT /fields (no validation needed)
        // Group changes by field to combine required+unique changes
        const fieldUpdates = new Map<string, { fieldId: string; is_required?: boolean; is_unique?: boolean }>();
        for (const change of constraintChanges) {
          const existing = fieldUpdates.get(change.fieldId) || { fieldId: change.fieldId };
          if (change.constraintType === 'required') {
            existing.is_required = change.toValue;
          } else {
            existing.is_unique = change.toValue;
          }
          fieldUpdates.set(change.fieldId, existing);
        }

        try {
          await updateConstraintsMutation.mutateAsync(Array.from(fieldUpdates.values()));
        } catch (error) {
          toast.operation.constraintChangeFailed(error instanceof Error ? error.message : undefined);
          // Clear saving states for failed updates
          setSavingConstraints((prev) => {
            const next = new Map(prev);
            for (const change of constraintChanges) {
              next.delete(getConstraintKey(change.fieldId, change.constraintType));
            }
            return next;
          });
        }
      } else {
        // Finalized tables: use /change-constraint endpoint (requires validation)
        // Separate relaxing (can run in parallel) from tightening (only one at a time)
        const relaxingChanges = constraintChanges.filter((c) => !c.toValue); // true → false
        const tighteningChanges = constraintChanges.filter((c) => c.toValue); // false → true

        // Process all relaxing changes (they complete synchronously)
        for (const change of relaxingChanges) {
          try {
            await changeConstraintMutation.mutateAsync({
              fieldId: change.fieldId,
              constraintType: change.constraintType,
              toValue: change.toValue,
            });
          } catch (error) {
            toast.operation.constraintChangeFailed(
              error instanceof Error ? error.message : undefined
            );
            setSavingConstraints((prev) => {
              const next = new Map(prev);
              next.delete(getConstraintKey(change.fieldId, change.constraintType));
              return next;
            });
          }
        }

        // Only process one tightening change at a time (async operations can't overlap)
        if (tighteningChanges.length > 0) {
          const firstChange = tighteningChanges[0];
          const remainingChanges = tighteningChanges.slice(1);

          // Put remaining changes back in pending (they'll be saved next time)
          if (remainingChanges.length > 0) {
            setPendingConstraints((prev) => {
              const next = new Map(prev);
              for (const change of remainingChanges) {
                next.set(getConstraintKey(change.fieldId, change.constraintType), change);
              }
              return next;
            });
            // Remove from saving state since they're back to pending
            setSavingConstraints((prev) => {
              const next = new Map(prev);
              for (const change of remainingChanges) {
                next.delete(getConstraintKey(change.fieldId, change.constraintType));
              }
              return next;
            });
            toast.info(
              'One constraint at a time',
              `${remainingChanges.length} constraint change(s) queued - save again after this operation completes`
            );
          }

          try {
            await changeConstraintMutation.mutateAsync({
              fieldId: firstChange.fieldId,
              constraintType: firstChange.constraintType,
              toValue: firstChange.toValue,
            });
          } catch (error) {
            toast.operation.constraintChangeFailed(
              error instanceof Error ? error.message : undefined
            );
            setSavingConstraints((prev) => {
              const next = new Map(prev);
              next.delete(getConstraintKey(firstChange.fieldId, firstChange.constraintType));
              return next;
            });
          }
        }
      }
    }

    // Save order changes
    if (pendingOrder) {
      try {
        await reorderFieldsMutation.mutateAsync(pendingOrder);
        setPendingOrder(null);
      } catch (error) {
        toast.error('Failed to update field order', error instanceof Error ? error.message : undefined);
      }
    }

    // Save enabled changes
    if (pendingEnabled.size > 0) {
      const enabledChanges = Array.from(pendingEnabled.entries());

      // Move to savingEnabled before mutations (so display shows target values)
      const newSavingEnabled = new Map(savingEnabled);
      for (const [fieldId, isEnabled] of enabledChanges) {
        newSavingEnabled.set(fieldId, isEnabled);
      }
      setSavingEnabled(newSavingEnabled);
      setPendingEnabled(new Map()); // Clear pending - they're now "saving"

      for (const [fieldId, isEnabled] of enabledChanges) {
        try {
          await updateFieldMutation.mutateAsync({
            fieldId,
            is_enabled: isEnabled,
          });
        } catch (error) {
          toast.error('Failed to update field', error instanceof Error ? error.message : undefined);
          // On error, remove from savingEnabled
          setSavingEnabled((prev) => {
            const next = new Map(prev);
            next.delete(fieldId);
            return next;
          });
        }
      }

    }

    // Save description changes
    if (pendingDescriptions.size > 0) {
      const descriptionChanges = Array.from(pendingDescriptions.entries());

      // Move to savingDescriptions before mutations (so display shows target values)
      const newSavingDescriptions = new Map(savingDescriptions);
      for (const [fieldId, description] of descriptionChanges) {
        newSavingDescriptions.set(fieldId, description);
      }
      setSavingDescriptions(newSavingDescriptions);
      setPendingDescriptions(new Map()); // Clear pending - they're now "saving"

      for (const [fieldId, description] of descriptionChanges) {
        try {
          await updateFieldMutation.mutateAsync({
            fieldId,
            description: description || null,
          });
        } catch (error) {
          toast.error('Failed to update description', error instanceof Error ? error.message : undefined);
          // On error, remove from savingDescriptions
          setSavingDescriptions((prev) => {
            const next = new Map(prev);
            next.delete(fieldId);
            return next;
          });
        }
      }

    }

    // Centralized cache invalidation after all schema changes
    invalidateSchemaQueries(queryClient, tableId);
  };

  const handleCancel = () => {
    setPendingChanges(new Map());
    setPendingConstraints(new Map());
    setPendingEnabled(new Map());
    setPendingDescriptions(new Map());
    setPendingOrder(null);
  };

  const handleAddField = () => {
    setIsAddFieldDialogOpen(true);
  };

  const hasPendingChanges = pendingChanges.size > 0 || pendingConstraints.size > 0 || pendingEnabled.size > 0 || pendingDescriptions.size > 0 || !!pendingOrder;
  const isPending = batchChangeTypeMutation.isPending || changeConstraintMutation.isPending || updateConstraintsMutation.isPending || reorderFieldsMutation.isPending || updateFieldMutation.isPending;
  const isSaving = savingTypes.size > 0 || savingConstraints.size > 0 || savingEnabled.size > 0 || savingDescriptions.size > 0;
  const isAwaitingConfirmation = awaitingTypeConfirmation.size > 0 || awaitingConstraintConfirmation !== null;

  // Build descriptive saving message based on what's being saved or active operations
  const getSavingMessage = (): string => {
    const messages: string[] = [];

    // Check local saving states first, then active operation, then awaiting confirmation
    if (savingTypes.size > 0) {
      const typeChanges = Array.from(savingTypes.entries());
      if (typeChanges.length === 1) {
        const [fieldId, toType] = typeChanges[0];
        const field = fields.find((f) => f.id === fieldId);
        messages.push(`Converting ${field?.name || 'field'} to ${toType}`);
      } else {
        messages.push(`Converting ${typeChanges.length} fields`);
      }
    } else if (activeConversions.size > 0) {
      const conversions = Array.from(activeConversions.entries());
      if (conversions.length === 1) {
        const [fieldId, toType] = conversions[0];
        const field = fields.find((f) => f.id === fieldId);
        messages.push(`Converting ${field?.name || 'field'} to ${toType}`);
      } else {
        messages.push(`Converting ${conversions.length} fields`);
      }
    } else if (awaitingTypeConfirmation.size > 0) {
      // Fall back to awaiting confirmation (operation completed, waiting for data refresh)
      const conversions = Array.from(awaitingTypeConfirmation.entries());
      if (conversions.length === 1) {
        const [fieldId, toType] = conversions[0];
        const field = fields.find((f) => f.id === fieldId);
        messages.push(`Converting ${field?.name || 'field'} to ${toType}`);
      } else {
        messages.push(`Converting ${conversions.length} fields`);
      }
    }

    if (savingConstraints.size > 0) {
      messages.push(`Updating constraints`);
    } else if (activeConstraintChange) {
      const field = fields.find((f) => f.id === activeConstraintChange.fieldId);
      const action = activeConstraintChange.toValue ? 'Enabling' : 'Disabling';
      messages.push(`${action} ${activeConstraintChange.constraintType} on ${field?.name || 'field'}`);
    } else if (awaitingConstraintConfirmation) {
      // Fall back to awaiting confirmation
      const field = fields.find((f) => f.id === awaitingConstraintConfirmation.fieldId);
      const action = awaitingConstraintConfirmation.toValue ? 'Enabling' : 'Disabling';
      messages.push(`${action} ${awaitingConstraintConfirmation.constraintType} on ${field?.name || 'field'}`);
    }

    if (savingEnabled.size > 0) {
      const enabledCount = Array.from(savingEnabled.values()).filter(Boolean).length;
      const disabledCount = savingEnabled.size - enabledCount;
      if (enabledCount > 0 && disabledCount > 0) {
        messages.push(`Updating ${savingEnabled.size} fields`);
      } else if (enabledCount > 0) {
        messages.push(`Enabling ${enabledCount} field${enabledCount > 1 ? 's' : ''}`);
      } else {
        messages.push(`Disabling ${disabledCount} field${disabledCount > 1 ? 's' : ''}`);
      }
    }

    if (savingDescriptions.size > 0) {
      messages.push(`Updating description${savingDescriptions.size > 1 ? 's' : ''}`);
    }

    if (reorderFieldsMutation.isPending) {
      messages.push(`Updating field order`);
    }

    return messages.length > 0 ? messages.join(', ') : 'Saving changes...';
  };
  const isDisabled = !canEdit || hasActiveOperation || isPending || isSaving;

  // Connector-managed tables: disable type changes but allow constraint edits
  const connectorType = useResolvedConnectorType(tableId, 'table');
  const isTypeDisabled = isDisabled || !!connectorType;

  // Get displayed values with pending changes applied
  // Priority: pending → saving → active operation → awaiting confirmation → actual value
  const getDisplayedType = (field: FieldRow): FieldDataType => {
    return pendingChanges.get(field.id)?.toType
      || savingTypes.get(field.id)
      || activeConversions.get(field.id)
      || awaitingTypeConfirmation.get(field.id)
      || field.dataType;
  };

  const getDisplayedConstraint = (field: FieldRow, constraintType: ConstraintType): boolean => {
    const key = getConstraintKey(field.id, constraintType);
    const originalValue = constraintType === 'unique' ? field.isUnique : field.isRequired;

    const pendingValue = pendingConstraints.get(key)?.toValue;
    if (pendingValue !== undefined) return pendingValue;

    const savingValue = savingConstraints.get(key);
    if (savingValue !== undefined) return savingValue;

    if (activeConstraintChange?.fieldId === field.id && activeConstraintChange.constraintType === constraintType) {
      return activeConstraintChange.toValue;
    }

    // Check awaiting confirmation (persists after operation completes until field data confirms)
    if (awaitingConstraintConfirmation?.fieldId === field.id && awaitingConstraintConfirmation.constraintType === constraintType) {
      return awaitingConstraintConfirmation.toValue;
    }

    return originalValue;
  };

  const hasTypeChange = (field: FieldRow): boolean => {
    return pendingChanges.has(field.id);
  };

  const getDisplayedEnabled = (field: FieldRow): boolean => {
    // Priority: pending > saving > original
    const pendingValue = pendingEnabled.get(field.id);
    if (pendingValue !== undefined) return pendingValue;
    const savingValue = savingEnabled.get(field.id);
    if (savingValue !== undefined) return savingValue;
    return field.isEnabled;
  };

  const hasEnabledChange = (field: FieldRow): boolean => {
    return pendingEnabled.has(field.id);
  };

  const hasConstraintChange = (field: FieldRow, constraintType: ConstraintType): boolean => {
    return pendingConstraints.has(getConstraintKey(field.id, constraintType));
  };

  // Get active field for drag overlay
  const activeField = useMemo(() => {
    if (!activeId) return null;
    return fields.find((f) => f.id === activeId) || null;
  }, [activeId, fields]);

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-2 py-4', variant === 'default' && 'px-4')}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // No table details
  if (!tableDetails) {
    return null;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header: Add Field + Search + Save/Cancel */}
      <div className="shrink-0 pb-3 px-4 grid grid-cols-3 items-center gap-4">
        {/* Left: Add Field + Show Disabled toggle */}
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddField}
              disabled={isDisabled}
              className="h-8"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Field
            </Button>
          )}
          <Toggle
            variant="outline"
            size="sm"
            pressed={showDisabledFields}
            onPressedChange={setShowDisabledFields}
            className="h-8 px-3"
          >
            {showDisabledFields ? (
              <>
                <Eye className="h-4 w-4" />
                Showing disabled
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4" />
                Hiding disabled
              </>
            )}
          </Toggle>
        </div>

        {/* Center: Search */}
        <div className="flex justify-center">
          {fields.length > 5 && (
            <div className="relative w-full max-w-xs">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
          )}
        </div>

        {/* Right: Save/Cancel buttons - editors/owners only */}
        <div className="flex items-center justify-end gap-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isPending || !hasPendingChanges}
                className="h-8 w-20"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isPending || !hasPendingChanges}
                className="h-8 w-20"
              >
                {isPending ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className={cn('flex-1 min-h-0 relative', variant === 'default' && 'px-4')}>
        {/* Loading overlay — positioned over the scroll container so it stays visible */}
        <div
          className={cn(
            'absolute inset-0 z-20 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm transition-all duration-300',
            (isPending || isSaving || hasActiveOperation || isAwaitingConfirmation)
              ? 'opacity-100'
              : 'opacity-0 pointer-events-none'
          )}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <span className="text-sm text-muted-foreground">{getSavingMessage()}</span>
        </div>
        <div className="h-full overflow-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full min-w-max border-collapse">
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="w-10 border-b-2" />
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-left text-sm font-medium whitespace-nowrap w-48">
                    Field
                  </th>
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-left text-sm font-medium whitespace-nowrap w-[130px]">
                    Type
                  </th>
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-center text-sm font-medium whitespace-nowrap w-20">
                    Enabled
                  </th>
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-center text-sm font-medium whitespace-nowrap w-20">
                    Required
                  </th>
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-center text-sm font-medium whitespace-nowrap w-20">
                    Unique
                  </th>
                  <th className="text-muted-foreground border-b-2 px-4 py-2 text-left text-sm font-medium whitespace-nowrap">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card">
                {filteredFields.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted-foreground px-4 py-8 text-center text-sm">
                      {searchTerm ? `No fields match "${searchTerm}"` : 'No fields defined'}
                    </td>
                  </tr>
                ) : (
                  <SortableContext
                    items={filteredFields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredFields.map((field) => (
                      <SortableFieldRow
                        key={field.id}
                        field={field}
                        displayedType={getDisplayedType(field)}
                        displayedEnabled={getDisplayedEnabled(field)}
                        displayedRequired={getDisplayedConstraint(field, 'required')}
                        displayedUnique={getDisplayedConstraint(field, 'unique')}
                        displayedDescription={pendingDescriptions.get(field.id) ?? savingDescriptions.get(field.id) ?? field.description ?? ''}
                        hasTypeChange={hasTypeChange(field)}
                        hasEnabledChange={hasEnabledChange(field)}
                        hasRequiredChange={hasConstraintChange(field, 'required')}
                        hasUniqueChange={hasConstraintChange(field, 'unique')}
                        hasDescriptionChange={pendingDescriptions.has(field.id)}
                        isDisabled={isDisabled}
                        isTypeDisabled={isTypeDisabled}
                        onTypeChange={handleTypeChange}
                        onEnabledToggle={handleEnabledToggle}
                        onConstraintToggle={handleConstraintToggle}
                        onDescriptionChange={handleDescriptionChange}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>

            {/* Drag Overlay */}
            <DragOverlay dropAnimation={null}>
              {activeField ? <DragOverlayRow field={activeField} /> : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* Add Field Dialog */}
      <AddFieldDialog
        tableId={tableId}
        existingFieldNames={fields.map((f) => f.name)}
        nextOrderIndex={fields.length}
        isOpen={isAddFieldDialogOpen}
        onClose={() => setIsAddFieldDialogOpen(false)}
      />
    </div>
  );
}
